import { createElement, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { clearRestaurantCart, selectCartCount } from '@/features/cart/cartSlice';
import { selectTableSession } from '@/features/tableSession/tableSessionSlice';

interface UseCartGuardResult {
  /**
   * Spec §3.1 cart guard: runs `action` right away when the cart is empty.
   * Otherwise opens the guard dialog ("Panier non envoyé pour la table N")
   * and only runs `action` if the waiter picks "Vider et changer".
   */
  guardedNavigate: (action: () => void) => void;
  /** Render this wherever the guard dialog should mount (null when closed). */
  dialog: ReactNode;
}

// This file is a plain .ts module (not .tsx) — the dialog markup is built
// with createElement rather than JSX so it still parses under the project's
// esbuild loader, which only enables JSX transform for .tsx files.
export function useCartGuard(restaurantUuid: string): UseCartGuardResult {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cartCount = useAppSelector(selectCartCount(restaurantUuid));
  const session = useAppSelector(selectTableSession);
  const hasLiveSession = session !== null && session.restaurantUuid === restaurantUuid;
  const tableNumber = hasLiveSession ? session.tableNumber : undefined;
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const guardedNavigate = (action: () => void) => {
    // SC-544 — the cart is per-restaurant and redux-persisted (localStorage),
    // while the table session lives in sessionStorage and is only rehydrated on
    // the /table/:tableToken route. Lines composed for a table therefore outlive
    // the session that could receive them (app restart, 清台, session expiry,
    // round already sent from another device), and the guard then blocked every
    // table switch with "Panier non envoyé pour la table" — naming no table,
    // because there is no session left to name. Such lines can no longer be sent
    // to any round (checkout needs a live guest token), so drop them and carry
    // on instead of raising a false "unsent round" alarm.
    if (!hasLiveSession) {
      if (cartCount > 0) dispatch(clearRestaurantCart({ restaurantUuid }));
      action();
      return;
    }
    if (cartCount > 0) {
      setPendingAction(() => action);
    } else {
      action();
    }
  };

  const stay = () => setPendingAction(null);

  const clearAndSwitch = () => {
    dispatch(clearRestaurantCart({ restaurantUuid }));
    const action = pendingAction;
    setPendingAction(null);
    action?.();
  };

  const dialog = pendingAction
    ? createElement(
        'div',
        {
          role: 'alertdialog',
          'aria-modal': 'true',
          'data-testid': 'cart-guard-dialog',
          className: 'fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-ink/75',
        },
        createElement(
          'div',
          { className: 'bg-paper border-2 border-ink rounded-btn p-5 max-w-sm w-full flex flex-col gap-4' },
          createElement(
            'p',
            { className: 'font-display text-base text-ink' },
            t('waiter.cartGuard.message', { table: tableNumber ?? '' }),
          ),
          createElement(
            'div',
            { className: 'flex justify-end gap-3' },
            createElement(
              'button',
              { type: 'button', onClick: stay, className: 'font-display text-sm underline' },
              t('waiter.cartGuard.stay'),
            ),
            createElement(
              'button',
              {
                type: 'button',
                onClick: clearAndSwitch,
                className:
                  'bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn-sm px-3 py-1.5 font-display text-sm',
              },
              t('waiter.cartGuard.clearAndSwitch'),
            ),
          ),
        ),
      )
    : null;

  return { guardedNavigate, dialog };
}
